import React from 'react';
import styled, { keyframes } from 'styled-components';

interface AcetatePlaceholderProps {
  size?: number;
  animated?: boolean;
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'animated'
})<{ size: number; animated: boolean }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  position: relative;
  border-radius: 50%;
  overflow: hidden;
  animation: ${props => props.animated ? spin : 'none'} 20s linear infinite;
  animation-play-state: paused;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  
  &:hover {
    animation-play-state: ${props => props.animated ? 'running' : 'paused'};
  }
`;

const VinylSVG = styled.svg`
  width: 100%;
  height: 100%;
`;

const AcetatePlaceholder: React.FC<AcetatePlaceholderProps> = ({ 
  size = 300, 
  animated = true 
}) => {
  return (
    <Container size={size} animated={animated}>
      <VinylSVG viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        {/* Main vinyl disc - semi-transparent */}
        <circle cx="100" cy="100" r="100" fill="#333333" opacity="0.7" />
        
        {/* Vinyl grooves - concentric circles */}
        <circle cx="100" cy="100" r="95" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="90" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="85" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="80" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="75" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="70" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="65" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="60" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="55" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="50" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="45" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="40" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="35" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="30" fill="none" stroke="#222222" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="25" fill="none" stroke="#222222" strokeWidth="0.5" />
        
        {/* Label in the middle */}
        <circle cx="100" cy="100" r="20" fill="#6A0DAD" />
        
        {/* Center hole */}
        <circle cx="100" cy="100" r="4" fill="#111111" />
        
        {/* Visual effects overlays */}
        <ellipse cx="100" cy="100" rx="95" ry="30" fill="rgba(255,255,255,0.1)" transform="rotate(45, 100, 100)" />
        <ellipse cx="100" cy="100" rx="95" ry="10" fill="rgba(255,255,255,0.1)" transform="rotate(135, 100, 100)" />
        
        {/* Text - "ACETATE" */}
        <text 
          x="100" 
          y="103" 
          fontSize="8" 
          fontFamily="Arial, sans-serif" 
          fontWeight="bold"
          fill="white" 
          textAnchor="middle" 
          dominantBaseline="middle"
        >
          ACETATE
        </text>
        
        {/* Small text - "RARE" */}
        <text 
          x="100" 
          y="93" 
          fontSize="4" 
          fontFamily="Arial, sans-serif" 
          fill="white" 
          textAnchor="middle" 
          dominantBaseline="middle"
        >
          RARE
        </text>
        
        {/* Radial gradient overlay for worn-out look */}
        <circle cx="100" cy="100" r="100" fill="url(#worn)" />
        
        {/* Reflections and scratches */}
        <defs>
          <radialGradient id="worn" cx="30%" cy="30%" r="100%" fx="30%" fy="30%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.1)" />
            <stop offset="50%" stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.3)" />
          </radialGradient>
        </defs>
      </VinylSVG>
    </Container>
  );
};

export default AcetatePlaceholder;